import React, { useState } from 'react';
import { Form, Input, Button, Card, message, Tabs, Alert, Row, Col, Typography } from 'antd';
import { UserOutlined, LockOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { authService, LoginData } from '../services/auth';

const { TabPane } = Tabs;
const { Title } = Typography;

export interface LoginFormValues {
  account: string;
  password: string;
}

export interface AdminLoginFormValues {
  username: string;
  password: string;
}

const Login: React.FC = () => {
  const [form] = Form.useForm();
  const [adminForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [adminError, setAdminError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'1' | '2'>('1');
  const navigate = useNavigate();

  const handleUserLogin = async (values: LoginFormValues) => {
    setLoading(true);
    setLoginError(null);

    try {
      const loginData: LoginData = {
        account: values.account.trim(),
        password: values.password
      };

      const response = await authService.login(loginData);
      const { access_token: token, user } = response;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify(user));

      message.success('登录成功！');
      // 根据用户角色跳转到不同的页面
      if (user.role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/user/dashboard');
      }
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '登录失败';
      setLoginError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleAdminLogin = async (values: AdminLoginFormValues) => {
    setLoading(true);
    setAdminError(null);

    try {
      const response = await authService.adminLogin(values);
      const { access_token: token, user: admin } = response;

      localStorage.setItem('token', token);
      localStorage.setItem('user', JSON.stringify({ ...admin, role: 'admin' }));

      message.success('管理员登录成功！');
      navigate('/admin/dashboard');
    } catch (error: any) {
      const errorMessage = error.response?.data?.message || '登录失败';
      setAdminError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-container">
      <Card className="login-form">
        <div style={{ textAlign: 'center', marginBottom: 16 }}>
          <Title level={2} style={{ margin: 0 }}>Evx管理系统</Title>
        </div>

        {loginError && (
          <Row style={{ marginBottom: 16 }}>
            <Col span={24}>
              <Alert
                message="登录失败"
                description={loginError}
                type="error"
                showIcon
                closable
                onClose={() => setLoginError(null)}
                icon={<ExclamationCircleOutlined />}
                action={
                  <Button size="small" onClick={() => setLoginError(null)}>
                    我知道了
                  </Button>
                }
              />
            </Col>
          </Row>
        )}

  

        <Tabs activeKey={activeTab} onChange={(key: string) => setActiveTab(key as '1' | '2')}>
          <TabPane tab="用户登录" key="1">
            <div className="login-content">
              <Form
                form={form}
                name="user_login"
                onFinish={handleUserLogin}
                autoComplete="off"
                layout="vertical"
                size="large"
              >
                <Form.Item
                  label="登录账号"
                  name="account"
                  rules={[
                    { required: true, message: '请输入用户名或邮箱' },
                    { validator: (_, value) => {
                      if (!value) return Promise.reject(new Error('请输入用户名或邮箱'));
                      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                      if (emailRegex.test(value) || (value.length >= 3 && value.length <= 20)) {
                        return Promise.resolve();
                      }
                      return Promise.reject(new Error('请输入有效的用户名（3-20位）或邮箱地址'));
                    }}
                  ]}
                >
                  <Input
                    prefix={<UserOutlined />}
                    placeholder="请输入用户名或邮箱"
                    maxLength={50}
                  />
                </Form.Item>

                <Form.Item
                  label="密码"
                  name="password"
                  rules={[{ required: true, message: '请输入密码' }]}
                >
                  <Input.Password
                    prefix={<LockOutlined />}
                    placeholder="请输入密码"
                    maxLength={50}
                  />
                </Form.Item>

                <Form.Item style={{ marginBottom: 0 }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    block
                    size="large"
                    style={{ marginTop: 8 }}
                  >
                    登录
                  </Button>
                </Form.Item>
              </Form>
            </div>
          </TabPane>

 
        </Tabs>
      </Card>
    </div>
  );
};

export default Login;